import React, { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Select } from '@bingads-webui-react/primitive-validation';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { i18nKeys } from '@bingads-webui-react/file-picker';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getUSTaxFromWithStateCode } from '../utils';
import { TaxTypeFormPropTypes } from '../shapes';
import { EmptyTaxForm } from './empty-taxform';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { registerTaxform } from '../taxform-store';
import { USViewTaxForm } from '../taxforms/viewtaxforms/us-viewtaxform';
import { FileScenarioMapping, requiredFeatures, USTaxCertPhase1States, USTaxCertStates } from '../constants';
import { TaxCertificateFilePicker } from '../controls';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  VatStatusEnum, TaxExemptionCertVerifiedResult,
} = Constants;
const {
  normalizeTaxExemptionCertVerifiedResult, getRequiredForCustomerClaimTaxExemption,
} = TaxUtils;

const getUSTaxSchema = () => ({
  name: 'US Taxation Validation Schema',
  type: 'object',
  properties: {
    IsTaxExemptionCertVerified: {
      // type is not bool because we have 3 status: Approve('True'), Reject('False'), Not set(null).
      // We use the `convertor` on the Select component to parse the string to the optional(bool).
      type: 'string',
    },
  },
});

const shouldDisplayTaxCertifications = taxCertifications => !!_.result(taxCertifications[0], 'displayName');

export const USCertTaxForm = ({
  i18n,
  onValidate,
  showLineByLineFormGroup,
  alwaysShowError,
  data,
  taxCertification,
  mtTaxCertification,
  showTaxApprovalForm,
  disabled,
  urlBuilder,
  showTaxCertLink,
  stateCode,
}) => {
  const validationI18n = useValidationI18n(i18n);
  const [certificationsState, setCertificationsState] = useState({
    certifications: [],
    isValid: false,
  });
  const customStrings = useMemo(() => ({
    [i18nKeys.FilePicker_ErrorMessage_Required]: i18n.getString(_TL_('Please upload your tax certificate that is associated with your VAT ID.')),
  }), [i18n]);

  const taxSchema = getUSTaxSchema();

  const [taxData, setTaxData] = useState(data);
  const isTaxCertPendingReview = mtTaxCertification.status === VatStatusEnum.Pending;
  const { taxCertifications: prefilledTaxCertifications } = taxCertification;
  const prefilledDisplayCertifications = shouldDisplayTaxCertifications(prefilledTaxCertifications) ? prefilledTaxCertifications : [];
  const additionalFormData = useMemo(() => ({ scenario: FileScenarioMapping[`US-${stateCode}`] }), [stateCode]);
  const helpPopupTopic = useMemo(() => {
    if (stateCode === USTaxCertStates.AK || stateCode === USTaxCertStates.HI) {
      return `pop_BA_Accounts_VATNumber_${stateCode}`;
    }
    return 'pop_BA_Accounts_VATNumber_NoTax';
  }, [stateCode]);
  const onTaxFormValidate = useCallback(({ data: vatTaxData }) => {
    setTaxData(vatTaxData);
  }, []);

  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const onCertificationsChange = useCallback(({ certifications, isValid }) => {
    setCertificationsState({
      certifications,
      isValid,
    });
  }, []);

  useEffect(() => {
    const taxCertifications = _.isEmpty(certificationsState.certifications) ? mtTaxCertification.taxCertifications : certificationsState.certifications;
    const isSuccessfullyUploaded = _.every(taxCertifications, cert => cert && cert.guid);
    const isValid = certificationsState.isValid && isSuccessfullyUploaded;

    onValidate({
      data: taxData,
      taxCertification: {
        taxCertifications,
      },
      isValid,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificationsState, taxData]);

  return (
    <div>
      <ValidationGroup
        validationProps={{
          i18n: validationI18n,
          data,
          onValidate: onTaxFormValidate,
          schema: taxSchema,
        }}
      >
        <TaxCertificateFilePicker
          i18n={i18n}
          customStrings={customStrings}
          certifications={prefilledDisplayCertifications}
          mtTaxCertification={mtTaxCertification}
          onCertificationsChange={onCertificationsChange}
          showLineByLineFormGroup={showLineByLineFormGroup}
          disabled={disabled}
          urlBuilder={urlBuilder}
          showTaxCertLink={showTaxCertLink}
          taxCertLabel={getRequiredForCustomerClaimTaxExemption(i18n.getString(_TL_('Tax exemption certificate')), i18n)}
          helpPopupTopic={helpPopupTopic}
          additionalFormData={additionalFormData}
        />
        <InlineInfoAlert>{i18n.getString(_TL_('Failure to provide your tax exemption certificate may result in tax being applied to your invoice.'))}</InlineInfoAlert>
        {
          showTaxApprovalForm && (
            <CustomizedFormGroup
              isStatic
              label={i18n.getString(_TL_('Tax certificate validation result'))}
            >
              <Select
                id="TaxCertificationValidationResult"
                disabled={disabled || !isTaxCertPendingReview}
                validationProps={{
                  field: 'IsTaxExemptionCertVerified',
                  alwaysShowError,
                  convertor: {
                    // NotSet value is null in backend, option only accepts strings, doing a mapping here
                    fromModel: value => normalizeTaxExemptionCertVerifiedResult(value) || 'NotSet',
                    toModel: normalizeTaxExemptionCertVerifiedResult,
                  },
                }}
              >
                <option value="NotSet">{i18n.getString(_TL_('(Not set)'))}</option>
                <option value={TaxExemptionCertVerifiedResult.Accept}>{i18n.getString(_TL_('Approve'))}</option>
                <option value={TaxExemptionCertVerifiedResult.Reject}>{i18n.getString(_TL_('Reject'))}</option>
              </Select>
            </CustomizedFormGroup>)
        }
      </ValidationGroup>
    </div>
  );
};

USCertTaxForm.propTypes = {
  ...TaxTypeFormPropTypes,
  stateCode: PropTypes.string.isRequired,
};

const statesPilotMapping = {
  AK: requiredFeatures.ABLAlaskaTax,
  HI: requiredFeatures.ABLHawaiiTax,
};

_.each(statesPilotMapping, (pilotFlag, stateCode) => {
  const taxform = withTaxFormByPilot({ pilotFlag, PilotForm: getUSTaxFromWithStateCode(USCertTaxForm, stateCode), DefaultForm: EmptyTaxForm });
  const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: getUSTaxFromWithStateCode(USViewTaxForm, stateCode), DefaultForm: EmptyViewTaxForm });

  registerTaxform(`/US/${stateCode}`, taxform, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: getUSTaxSchema }));
});

USTaxCertPhase1States.forEach((stateCode) => {
  const pilotFlag = requiredFeatures.ABLUSTaxCertPhase1;
  const taxform = withTaxFormByPilot({ pilotFlag, PilotForm: getUSTaxFromWithStateCode(USCertTaxForm, stateCode), DefaultForm: EmptyTaxForm });
  const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: getUSTaxFromWithStateCode(USViewTaxForm, stateCode), DefaultForm: EmptyViewTaxForm });

  registerTaxform(`/US/${stateCode}`, taxform, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: getUSTaxSchema }));
});
